// CommandSubmitForm – modal for sending bulk commands to selected devices.

import React, { JSX, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { CommandOperation, logKind } from '../../../types/Commands';
import {
    CMD_REAR_FW_DWNLD,
    CMD_REAR_LOG_UPLD,
    CMD_REAR_DELETE_LOGS,
    CMD_REAR_TOGGLE_LED,
    CMD_REAR_APP_MODE,
    CMD_REAR_RACE_MODE,
    CMD_REAR_SLEEP,
    CMD_REAR_REBOOT,
    CMD_REAR_WINTER,
    CMD_REAR_MODEM_DFOTA,
    CMD_FRONT_FW_DWNLD,
    CMD_FRONT_LOG_UPLD,
    CMD_FRONT_DELETE_LOGS,
    CMD_FRONT_TOGGLE_LED,
} from '../../../types/Commands';
import type { S3ObjectInfo } from '../../../types/AWSTypes';
import { useAuth } from '../../context/AuthContext';
import {
  formatErrorMessage,
  showToastError,
  showToastErrorMessage,
} from '../../helpers/toastError';
import RefreshButton from '../shared/RefreshButton';
import InlineSpinner from '../shared/InlineSpinner';
import CyclingLoader from '../shared/CyclingLoader';
import { PortaledLayer } from '../shared/PortaledLayer';
import { parseFirmwareListPayload } from '../../helpers/firmwareArtifacts';
import { isFrontDeviceId, isRearDeviceId } from '../../helpers/devicePairing';

// ---- Types ----

interface CommandSubmitFormProps {
    selectedIds: string[];
    operatorScopedEventId?: string | null;
    onClose: () => void;
    onConfirm: (params: {
        operation: CommandOperation;
        deviceIds: string[];
        requestId?: string;
    }) => void;
}

// ---- Constants ----

const CMD_FW_DWNLD    = CMD_REAR_FW_DWNLD;
const CMD_LOG_UPLD    = CMD_REAR_LOG_UPLD;
const CMD_APPMODE     = CMD_REAR_APP_MODE;
const CMD_RACEMODE    = CMD_REAR_RACE_MODE;
const CMD_SLEEP       = CMD_REAR_SLEEP;
const CMD_REBOOT      = CMD_REAR_REBOOT;
const CMD_DELETE_LOGS = CMD_REAR_DELETE_LOGS;
const CMD_TOGGLELED   = CMD_REAR_TOGGLE_LED;
const CMD_WINTER      = CMD_REAR_WINTER;
const CMD_MODEM_DFOTA = CMD_REAR_MODEM_DFOTA;

const LOG_RACE: logKind = 'race';
const LOG_DEBUG: logKind = 'debug';
const LOG_FOLDER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

type AdminLogUploadIndexMode = 'event' | 'folder' | 'neither';

function parseEventsForSelect(data: unknown): { eventId: string; name: string }[] {
    const raw = Array.isArray(data)
        ? data
        : data && typeof data === 'object' && Array.isArray((data as { events?: unknown }).events)
          ? (data as { events: unknown[] }).events
          : [];
    return raw
        .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
        .map((r) => ({
            eventId: String(r.eventId ?? '').trim(),
            name: String(r.name ?? '').trim(),
        }))
        .filter((r) => r.eventId);
}

/** Devices actually selected that match the given role. Deliberately does NOT
 *  derive/include a device's paired counterpart (e.g. a selected rear's front) —
 *  only devices the user explicitly selected should ever appear as command targets. */
function mapIdsToRole(selected: string[], role: 'front' | 'rear'): string[] {
    const check = role === 'front' ? isFrontDeviceId : isRearDeviceId;
    const out = new Set<string>();
    for (const raw of selected) {
        const id = String(raw ?? '').trim();
        if (id && check(id)) out.add(id);
    }
    return Array.from(out);
}

function selectionHasFrontIds(selected: string[]): boolean {
    return selected.some((id) => isFrontDeviceId(String(id ?? '').trim()));
}

function selectionHasRearIds(selected: string[]): boolean {
    return selected.some((id) => isRearDeviceId(String(id ?? '').trim()));
}

function needsFrontRearRoleChoice(selected: string[]): boolean {
    return selectionHasFrontIds(selected) && selectionHasRearIds(selected);
}

function defaultCommandRoleForSelection(selected: string[]): 'front' | 'rear' {
    if (selectionHasFrontIds(selected) && !selectionHasRearIds(selected)) return 'front';
    return 'rear';
}

// ---- Shared style tokens ----

const SELECT_CLS =
    'w-full rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm ' +
    'focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 ' +
    'dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-500';

const INPUT_CLS =
    'w-full rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm ' +
    'placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 ' +
    'dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-500';

const LABEL_CLS = 'block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-1';

const BTN_GHOST =
    'rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 ' +
    'hover:bg-gray-50 transition-colors dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700';

// ---- Component ----

export default function CommandSubmitForm(props: CommandSubmitFormProps): JSX.Element {
    const { selectedIds, operatorScopedEventId = null, onClose, onConfirm } = props;
    const { isAdmin, isOperator } = useAuth();

    const [operation, setOperation] = useState<CommandOperation>(CMD_APPMODE);
    const [targetVersion, setTargetVersion] = useState<string>('');
    const [logKind, setLogKind] = useState<logKind>(LOG_RACE);
    const [logFolderMode, setLogFolderMode] = useState<'none' | 'existing' | 'new'>('none');
    const [logUploadIndexMode, setLogUploadIndexMode] = useState<AdminLogUploadIndexMode>('neither');
    const [existingLogFolders, setExistingLogFolders] = useState<string[]>([]);
    const [existingLogFolder, setExistingLogFolder] = useState<string>('');
    const [newLogFolder, setNewLogFolder] = useState<string>('');
    const [logFoldersLoading, setLogFoldersLoading] = useState(false);
    const [logFoldersError, setLogFoldersError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const [adminOptionalEventId, setAdminOptionalEventId] = useState('');
    const [adminEvents, setAdminEvents] = useState<{ eventId: string; name: string }[]>([]);
    const [adminEventsLoading, setAdminEventsLoading] = useState(false);
    const [adminEventsError, setAdminEventsError] = useState<string | null>(null);
    const [logUploadRaces, setLogUploadRaces] = useState<{ raceId: string; name?: string }[]>([]);
    const [logUploadRaceId, setLogUploadRaceId] = useState('');
    const [logUploadRacesLoading, setLogUploadRacesLoading] = useState(false);
    const [logUploadRacesError, setLogUploadRacesError] = useState<string | null>(null);

    const [registeredDeviceIds, setRegisteredDeviceIds] = useState<Set<string> | null>(null);
    const [deviceMembershipLoading, setDeviceMembershipLoading] = useState(false);
    const [deviceMembershipError, setDeviceMembershipError] = useState<string | null>(null);

    const [modemFwUrl, setModemFwUrl] = useState<string>('');
    const [modemFwTargets, setModemFwTargets] = useState<{ key: string; fileName: string; url: string }[]>([]);
    const [modemFwLoading, setModemFwLoading] = useState(false);
    const [modemFwError, setModemFwError] = useState<string | null>(null);
    const [fwTargets, setFwTargets] = useState<{ targetVersion: string; fileName: string; version: string; role: 'front' | 'rear' }[]>([]);
    const [fwLoading, setFwLoading] = useState(false);
    const [fwError, setFwError] = useState<string | null>(null);
    const [targetDeviceIds, setTargetDeviceIds] = useState<string[]>(() =>
        Array.from(new Set(selectedIds.map((id) => String(id ?? '').trim()).filter(Boolean))),
    );
    const [targetSearch, setTargetSearch] = useState('');
    const showRoleChoice = useMemo(() => needsFrontRearRoleChoice(selectedIds), [selectedIds]);
    const [commandRole, setCommandRole] = useState<'front' | 'rear' | null>(() =>
        showRoleChoice ? null : defaultCommandRoleForSelection(selectedIds),
    );
    const [roleStep, setRoleStep] = useState<'choose' | 'form'>(() =>
        showRoleChoice ? 'choose' : 'form',
    );

    useEffect(() => {
        if (needsFrontRearRoleChoice(selectedIds)) {
            setRoleStep('choose');
            setCommandRole(null);
            return;
        }
        const role = defaultCommandRoleForSelection(selectedIds);
        setRoleStep('form');
        setCommandRole(role);
    }, [selectedIds]);

    useEffect(() => {
        if (commandRole === 'front' || commandRole === 'rear') {
            setTargetDeviceIds(mapIdsToRole(selectedIds, commandRole));
            return;
        }
        setTargetDeviceIds(Array.from(new Set(selectedIds.map((id) => String(id ?? '').trim()).filter(Boolean))));
    }, [selectedIds, commandRole]);

    const frontEligibleIds = useMemo(() => mapIdsToRole(selectedIds, 'front'), [selectedIds]);
    const rearEligibleIds  = useMemo(() => mapIdsToRole(selectedIds, 'rear'),  [selectedIds]);

    async function refreshFwVersions(role: 'rear' | 'front' = commandRole === 'front' ? 'front' : 'rear'): Promise<void> {
        setFwLoading(true);
        setFwError(null);
        try {
            const loadRows = async (requestedRole: 'front' | 'rear') => {
                const res = await window.eventsAPI.getFirmwareVersions(requestedRole);
                const { artifacts } = parseFirmwareListPayload(res, { defaultRole: requestedRole });
                return artifacts.map((a) => ({
                    targetVersion: String(a.key || `${requestedRole}/${a.fileName}`).trim(),
                    fileName: a.fileName,
                    version: a.version,
                    role: requestedRole,
                }));
            };
            const rows = await loadRows(role);
            setFwTargets(rows);
            if (!rows.some((r) => r.targetVersion === targetVersion)) setTargetVersion('');
            if (rows.length === 0) {
                const msg = role === 'front'
                    ? 'No front firmware found — upload a .bin to the front/ prefix first'
                    : 'No firmware versions found';
                setFwError(msg);
                showToastErrorMessage(msg);
            }
        } catch (err: any) {
            const msg = formatErrorMessage(err, 'Failed to load firmware versions');
            setFwError(msg);
            setFwTargets([]);
            showToastErrorMessage(msg);
        } finally {
            setFwLoading(false);
        }
    }

    useEffect(() => {
        if (operation !== CMD_FW_DWNLD) return;
        void refreshFwVersions(commandRole === 'front' ? 'front' : 'rear');
    }, [operation, commandRole]);

    async function refreshModemFwPackages(): Promise<void> {
        setModemFwLoading(true);
        setModemFwError(null);
        try {
            const res = await window.eventsAPI.getModemFirmwareVersions();
            if (!res?.ok) throw new Error(res?.error || 'Failed to load modem firmware packages');
            const raw = Array.isArray((res.data as { artifacts?: unknown[] } | undefined)?.artifacts)
                ? (res.data as { artifacts: unknown[] }).artifacts
                : [];
            const rows = raw
                .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
                .map((r) => ({
                    key: String(r.key ?? '').trim(),
                    fileName: String(r.fileName ?? r.key ?? '').trim(),
                    url: String(r.url ?? '').trim(),
                }))
                .filter((r) => r.url && r.fileName);
            setModemFwTargets(rows);
            if (!rows.some((r) => r.url === modemFwUrl)) setModemFwUrl('');
            if (rows.length === 0) {
                const msg = 'No modem firmware packages found — upload a .par to the modem firmware bucket first';
                setModemFwError(msg);
                showToastErrorMessage(msg);
            }
        } catch (err: any) {
            const msg = formatErrorMessage(err, 'Failed to load modem firmware packages');
            setModemFwError(msg);
            setModemFwTargets([]);
            showToastErrorMessage(msg);
        } finally {
            setModemFwLoading(false);
        }
    }

    useEffect(() => {
        if (operation !== CMD_MODEM_DFOTA) return;
        void refreshModemFwPackages();
    }, [operation]);

    const shouldLoadAdminEvents = isAdmin && operation === CMD_LOG_UPLD;

    useEffect(() => {
        if (!shouldLoadAdminEvents) return;
        let cancelled = false;
        setAdminEventsLoading(true);
        setAdminEventsError(null);
        void (async () => {
            try {
                const res = await window.eventsAPI.listEvents({ limit: 200 });
                if (!res?.ok) throw new Error(res?.error || 'Failed to load events');
                if (!cancelled) setAdminEvents(parseEventsForSelect(res.data));
            } catch (err: any) {
                if (!cancelled) { setAdminEvents([]); setAdminEventsError(formatErrorMessage(err, 'Failed to load events')); }
            } finally {
                if (!cancelled) setAdminEventsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [shouldLoadAdminEvents]);

    const logUploadEventIdForRace =
        operation === CMD_LOG_UPLD
            ? isAdmin
                ? logUploadIndexMode === 'event' ? String(adminOptionalEventId ?? '').trim() : ''
                : isOperator && !isAdmin ? String(operatorScopedEventId ?? '').trim() : ''
            : '';

    useEffect(() => {
        const eventId = String(logUploadEventIdForRace || '').trim();
        if (!eventId) { setLogUploadRaces([]); setLogUploadRaceId(''); setLogUploadRacesError(null); setLogUploadRacesLoading(false); return; }
        if (!window.eventsAPI?.listEventRaces) { setLogUploadRaces([]); setLogUploadRaceId(''); return; }
        let cancelled = false;
        setLogUploadRacesLoading(true);
        setLogUploadRacesError(null);
        void window.eventsAPI.listEventRaces(eventId)
            .then((res: any) => {
                if (cancelled) return;
                if (!res?.ok) { setLogUploadRaces([]); setLogUploadRaceId(''); setLogUploadRacesError(String(res?.error || 'Failed to load races')); return; }
                const rows = Array.isArray(res?.data?.races) ? res.data.races : [];
                const mapped = rows.map((x: any) => ({ raceId: String(x?.raceId ?? '').trim(), name: x?.name != null ? String(x.name) : undefined })).filter((x: { raceId: string }) => x.raceId);
                setLogUploadRaces(mapped);
                setLogUploadRaceId((prev) => (prev && mapped.some((x: { raceId: string }) => x.raceId === prev) ? prev : ''));
            })
            .catch((err: any) => { if (!cancelled) { setLogUploadRaces([]); setLogUploadRaceId(''); setLogUploadRacesError(formatErrorMessage(err, 'Failed to load races')); } })
            .finally(() => { if (!cancelled) setLogUploadRacesLoading(false); });
        return () => { cancelled = true; };
    }, [logUploadEventIdForRace]);

    // When logs are being indexed under an event (and optionally a specific race), fetch which
    // devices are actually registered there so we can flag target devices that aren't — a device
    // that was never assigned to that race must not have its logs written into that race's folder.
    const effectiveLogUploadRaceId = operation === CMD_LOG_UPLD ? String(logUploadRaceId ?? '').trim() : '';
    const membershipCheckActive = operation === CMD_LOG_UPLD && !!logUploadEventIdForRace;

    async function refreshDeviceMembership(): Promise<void> {
        if (!membershipCheckActive) {
            setRegisteredDeviceIds(null);
            setDeviceMembershipError(null);
            setDeviceMembershipLoading(false);
            return;
        }
        if (!window.eventsAPI?.listEventDevices) {
            setRegisteredDeviceIds(null);
            setDeviceMembershipError('Event devices API unavailable.');
            return;
        }
        setDeviceMembershipLoading(true);
        setDeviceMembershipError(null);
        try {
            const res: any = await window.eventsAPI.listEventDevices(logUploadEventIdForRace, {
                limit: 5000,
                raceId: effectiveLogUploadRaceId || undefined,
            });
            if (!res?.ok) {
                setRegisteredDeviceIds(null);
                setDeviceMembershipError(String(res?.error || 'Failed to check device registration'));
                return;
            }
            const rows = Array.isArray(res?.data?.devices) ? res.data.devices : [];
            const ids = new Set<string>(
                rows.map((d: any) => String(d?.deviceId ?? '').trim()).filter(Boolean),
            );
            setRegisteredDeviceIds(ids);
        } catch (err: any) {
            setRegisteredDeviceIds(null);
            setDeviceMembershipError(formatErrorMessage(err, 'Failed to check device registration'));
        } finally {
            setDeviceMembershipLoading(false);
        }
    }

    useEffect(() => {
        void refreshDeviceMembership();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [membershipCheckActive, logUploadEventIdForRace, effectiveLogUploadRaceId]);

    function applyAdminLogUploadIndexMode(next: AdminLogUploadIndexMode): void {
        setLogUploadIndexMode(next);
        if (next === 'event') { setLogFolderMode('none'); setExistingLogFolder(''); setNewLogFolder(''); }
        else if (next === 'folder') { setAdminOptionalEventId(''); setLogUploadRaceId(''); setLogFolderMode((prev) => (prev === 'none' ? 'existing' : prev)); }
        else { setAdminOptionalEventId(''); setLogUploadRaceId(''); setLogFolderMode('none'); setExistingLogFolder(''); setNewLogFolder(''); }
    }

    function extractLogFolderFromObject(row: S3ObjectInfo & { logFolder?: unknown }, expectedLogKind: logKind): string {
        const explicit = typeof row.logFolder === 'string' ? row.logFolder.trim() : '';
        if (explicit) return explicit;
        const parts = String(row.key || '').split('/').filter(Boolean);
        if (parts.length < 4) return '';
        if (parts[1] !== expectedLogKind) return '';
        return parts[2] ?? '';
    }

    async function refreshExistingLogFolders(): Promise<void> {
        if (!window.fleetAPI?.listLogFiles) { setExistingLogFolders([]); setExistingLogFolder(''); return; }
        const uniqueDeviceIds = Array.from(new Set(targetDeviceIds.map((id) => String(id || '').trim()).filter(Boolean)));
        if (uniqueDeviceIds.length === 0) { setExistingLogFolders([]); setExistingLogFolder(''); return; }
        setLogFoldersLoading(true);
        setLogFoldersError(null);
        try {
            const eventId =
                isOperator && !isAdmin ? String(operatorScopedEventId ?? '').trim() || undefined
                : isAdmin && operation === CMD_LOG_UPLD && logUploadIndexMode === 'folder' ? undefined
                : isAdmin && String(adminOptionalEventId ?? '').trim() ? String(adminOptionalEventId).trim() : undefined;
            const sampleIds = uniqueDeviceIds.slice(0, 20);
            const responses = await Promise.all(sampleIds.map((deviceId) => window.fleetAPI.listLogFiles({ deviceId, logType: logKind, eventId })));
            const found = new Set<string>();
            for (const rows of responses) for (const row of rows) { const folder = extractLogFolderFromObject(row as S3ObjectInfo & { logFolder?: unknown }, logKind); if (folder) found.add(folder); }
            const sorted = Array.from(found).sort((a, b) => a.localeCompare(b));
            setExistingLogFolders(sorted);
            setExistingLogFolder((prev) => (prev && sorted.includes(prev) ? prev : sorted[0] ?? ''));
        } catch (err: any) {
            setExistingLogFolders([]); setExistingLogFolder(''); setLogFoldersError(formatErrorMessage(err, 'Failed to load existing log folders'));
        } finally { setLogFoldersLoading(false); }
    }

    useEffect(() => {
        if (operation !== CMD_LOG_UPLD) return;
        if (logFolderMode !== 'existing') return;
        void refreshExistingLogFolders();
    }, [operation, logFolderMode, logKind, isAdmin, isOperator, operatorScopedEventId, adminOptionalEventId, logUploadIndexMode, selectedIds, targetDeviceIds]);

    const rearCount  = useMemo(() => rearEligibleIds.length,  [rearEligibleIds]);
    const frontCount = useMemo(() => frontEligibleIds.length, [frontEligibleIds]);
    const filteredTargetDeviceIds = useMemo(() => {
        const query = targetSearch.trim().toLowerCase();
        return targetDeviceIds.filter((id) => (!query || id.toLowerCase().includes(query)));
    }, [targetDeviceIds, targetSearch]);

    const unregisteredTargetIds = useMemo(() => {
        if (!membershipCheckActive || !registeredDeviceIds) return [];
        return targetDeviceIds.filter((id) => !registeredDeviceIds.has(id));
    }, [membershipCheckActive, registeredDeviceIds, targetDeviceIds]);
    const unregisteredTargetIdSet = useMemo(() => new Set(unregisteredTargetIds), [unregisteredTargetIds]);

    const membershipDestinationLabel = useMemo(() => {
        if (effectiveLogUploadRaceId) {
            const race = logUploadRaces.find((r) => r.raceId === effectiveLogUploadRaceId);
            return race?.name ? `race "${race.name}"` : `race ${effectiveLogUploadRaceId}`;
        }
        if (isAdmin) {
            const ev = adminEvents.find((e) => e.eventId === logUploadEventIdForRace);
            return ev?.name ? `event "${ev.name}"` : `event ${logUploadEventIdForRace}`;
        }
        return 'this event';
    }, [effectiveLogUploadRaceId, logUploadRaces, isAdmin, adminEvents, logUploadEventIdForRace]);

    function removeTargetDevice(deviceId: string): void {
        const id = String(deviceId ?? '').trim();
        if (!id) return;
        setTargetDeviceIds((prev) => prev.filter((existing) => existing !== id));
    }

    function removeUnregisteredDevices(): void {
        if (!unregisteredTargetIdSet.size) return;
        setTargetDeviceIds((prev) => prev.filter((existing) => !unregisteredTargetIdSet.has(existing)));
    }

    async function submitWithDevices(deviceIdsToSubmit: string[], options?: { closeOnSuccess?: boolean }): Promise<boolean> {
        const effectiveIds = Array.from(new Set(deviceIdsToSubmit.map((id) => String(id ?? '').trim()).filter(Boolean)));
        if (!effectiveIds.length || submitting) return false;
        const closeOnSuccess = options?.closeOnSuccess ?? true;

        if (isOperator && !isAdmin && !String(operatorScopedEventId ?? '').trim()) {
            showToastErrorMessage( 'Select an event before sending operator commands.');
            return false;
        }
        if (operation === CMD_FW_DWNLD && !targetVersion) {
            showToastErrorMessage( 'Please select a firmware target version before submitting.');
            return false;
        }
        if (membershipCheckActive && registeredDeviceIds) {
            const notRegistered = effectiveIds.filter((id) => !registeredDeviceIds.has(id));
            if (notRegistered.length) {
                showToastErrorMessage(
                    `${notRegistered.length} device${notRegistered.length === 1 ? '' : 's'} not registered to ${membershipDestinationLabel} — remove ${notRegistered.length === 1 ? 'it' : 'them'} or choose a different destination.`,
                );
                return false;
            }
        }

        setSubmitting(true);
        try {
            const chosenNewFolder = newLogFolder.trim();
            const chosenExistingFolder = existingLogFolder.trim();
            let logFolder: string | undefined;
            if (operation === CMD_LOG_UPLD) {
                if (isAdmin) {
                    if (logUploadIndexMode === 'folder') {
                        if (logFolderMode === 'existing') {
                            if (!chosenExistingFolder) { showToastErrorMessage( 'Select an existing folder name.'); return false; }
                            logFolder = chosenExistingFolder;
                        } else if (logFolderMode === 'new') {
                            if (!chosenNewFolder) { showToastErrorMessage( 'Enter a new folder name.'); return false; }
                            if (!LOG_FOLDER_PATTERN.test(chosenNewFolder)) { showToastErrorMessage( 'Folder name must be 1-64 chars: letters, numbers, dot, underscore, hyphen.'); return false; }
                            logFolder = chosenNewFolder;
                        } else { showToastErrorMessage( 'Choose Use existing folder or Create new folder.'); return false; }
                    }
                    if (logUploadIndexMode === 'event' && !String(adminOptionalEventId ?? '').trim()) {
                        showToastErrorMessage( 'Select an event, or change indexing to folder-only / fleet-wide.'); return false;
                    }
                } else if (logFolderMode === 'existing') {
                    if (!chosenExistingFolder) { showToastErrorMessage( 'Select an existing folder or choose None.'); return false; }
                    logFolder = chosenExistingFolder;
                } else if (logFolderMode === 'new') {
                    if (!chosenNewFolder) { showToastErrorMessage( 'Enter a folder name or choose None.'); return false; }
                    if (!LOG_FOLDER_PATTERN.test(chosenNewFolder)) { showToastErrorMessage( 'Folder name must be 1-64 chars: letters, numbers, dot, underscore, hyphen.'); return false; }
                    logFolder = chosenNewFolder;
                }
            }

            const adminEventForCmd =
                isAdmin && operation === CMD_DELETE_LOGS ? ''
                : isAdmin && operation === CMD_LOG_UPLD && logUploadIndexMode !== 'event' ? ''
                : isAdmin ? String(adminOptionalEventId ?? '').trim() : '';
            const raceIdForCmd = operation === CMD_LOG_UPLD ? String(logUploadRaceId ?? '').trim() || undefined : undefined;

            if (operation === CMD_MODEM_DFOTA) {
                const url = modemFwUrl.trim();
                if (!url) { showToastErrorMessage('Select a modem firmware package'); return false; }
                const urlLower = url.toLowerCase();
                if (!urlLower.startsWith('https://') && !urlLower.startsWith('http://') && !urlLower.startsWith('ftp://')) { showToastErrorMessage('Modem firmware URL must be HTTP, HTTPS, or FTP'); return false; }
                if (url.length > 255) { showToastErrorMessage('URL exceeds 255-character modem limit'); return false; }
            }

            const result = await window.fleetAPI.submitCommand({
                operation,
                deviceIds: effectiveIds,
                eventId: isOperator && !isAdmin ? String(operatorScopedEventId ?? '').trim() : adminEventForCmd || undefined,
                raceId: raceIdForCmd,
                targetVersion: operation === CMD_FW_DWNLD ? targetVersion : undefined,
                commandTarget: commandRole ?? undefined,
                logKind: operation === CMD_LOG_UPLD || operation === CMD_DELETE_LOGS ? logKind : undefined,
                logFolder: operation === CMD_LOG_UPLD ? logFolder : undefined,
                modemFwUrl: operation === CMD_MODEM_DFOTA ? modemFwUrl.trim() : undefined,
            });

            if (!result.ok) {
                showToastErrorMessage( result.error || 'Command submit failed');
                return false;
            }

            const anyData = result.data as any;
            const requestId = anyData && typeof anyData === 'object' && typeof anyData.requestId === 'string' ? anyData.requestId : undefined;
            const message = requestId
                ? `Request accepted. Request ID ${requestId}. The device list has been automatically filtered per your request. `
                : 'Request accepted.';
            window.dispatchEvent(new CustomEvent('toast-info', { detail: { message, requestId } }));
            onConfirm({ operation, deviceIds: effectiveIds, requestId });
            if (closeOnSuccess) onClose();
            return true;
        } finally {
            setSubmitting(false);
        }
    }

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
        e.preventDefault();
        if (!targetDeviceIds.length || submitting) return;
        await submitWithDevices(targetDeviceIds);
    }

    const isFirmwareOp  = operation === CMD_FW_DWNLD;
    const isLogKindOp   = operation === CMD_LOG_UPLD || operation === CMD_DELETE_LOGS;
    const isLogUploadOp = operation === CMD_LOG_UPLD;
    const isModemDfotaOp = operation === CMD_MODEM_DFOTA;

    const firmwareDisabled = isFirmwareOp && (!fwTargets.length || !targetVersion || fwLoading || !!fwError);
    const modemDfotaDisabled = isModemDfotaOp && (!modemFwTargets.length || !modemFwUrl.trim() || modemFwLoading || !!modemFwError);
    const membershipBlocked = membershipCheckActive && (deviceMembershipLoading || !!deviceMembershipError || unregisteredTargetIds.length > 0);
    const submitDisabled   = targetDeviceIds.length === 0 || submitting || firmwareDisabled || modemDfotaDisabled || membershipBlocked;

    const OP_GROUPS_ALL = [
        { label: 'Firmware', options: [{ value: CMD_FW_DWNLD, label: 'Download Firmware' }, { value: CMD_MODEM_DFOTA, label: 'Modem DFOTA' }] },
        { label: 'Logs',     options: [{ value: CMD_LOG_UPLD, label: 'Upload Logs' }, { value: CMD_DELETE_LOGS, label: 'Delete Logs' }] },
        { label: 'Actions',  options: [
            { value: CMD_TOGGLELED, label: 'Toggle LED' },
            { value: CMD_APPMODE,   label: 'App mode' },
            { value: CMD_RACEMODE,  label: 'Race mode' },
            { value: CMD_SLEEP,     label: 'Sleep / Shutdown' },
            { value: CMD_REBOOT,    label: 'Reboot' },
            { value: CMD_WINTER,    label: 'Winter mode' },
        ]},
    ];
    const OP_GROUPS_FRONT = [
        { label: 'Firmware', options: [{ value: CMD_FW_DWNLD, label: 'Download Firmware' }] },
        { label: 'Logs',     options: [{ value: CMD_LOG_UPLD, label: 'Upload Logs' }, { value: CMD_DELETE_LOGS, label: 'Delete Logs' }] },
        { label: 'Actions',  options: [{ value: CMD_TOGGLELED, label: 'Toggle LED' }] },
    ];
    const opGroups = commandRole === 'front' ? OP_GROUPS_FRONT : OP_GROUPS_ALL;

    useEffect(() => {
        if (commandRole === 'front' && operation !== CMD_FW_DWNLD && operation !== CMD_LOG_UPLD && operation !== CMD_DELETE_LOGS && operation !== CMD_TOGGLELED) {
            setOperation(CMD_LOG_UPLD);
        }
    }, [commandRole, operation]);

    function proceedWithRole(role: 'front' | 'rear'): void {
        setCommandRole(role);
        setTargetDeviceIds(mapIdsToRole(selectedIds, role));
        setTargetSearch('');
        setRoleStep('form');
    }

    // ---- Render ----

    return (
        <PortaledLayer>
            <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
                <div className="relative flex w-full max-w-md flex-col rounded-xl border border-gray-200/80 bg-white shadow-2xl dark:border-slate-700/60 dark:bg-slate-900"
                     style={{ maxHeight: 'calc(100vh - 2rem)' }}>

                    {submitting ? (
                        <div
                            className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/85 backdrop-blur-sm dark:bg-slate-900/85"
                            aria-live="polite"
                            aria-busy="true"
                        >
                            <CyclingLoader
                                phrases={['Submitting command…', 'Waiting for server…', 'Sending to devices…']}
                                size="md"
                            />
                        </div>
                    ) : null}

                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-4">
                        <div>
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Submit Command</h2>
                            {commandRole && (
                                <p className="mt-0.5 text-[11px] text-gray-400 dark:text-slate-500">
                                    {commandRole === 'front' ? 'Front' : 'Rear'} devices · {targetDeviceIds.length} target{targetDeviceIds.length !== 1 ? 's' : ''}
                                </p>
                            )}
                        </div>
                        <button type="button" onClick={onClose} disabled={submitting}
                            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="h-px bg-gray-100 dark:bg-slate-800" />

                    {/* Role chooser step */}
                    {roleStep === 'choose' ? (
                        <div className="flex flex-col gap-4 px-5 py-5">
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                Your selection includes both front and rear devices. Choose which role to target — commands can only be sent to one role at a time.
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <button type="button" onClick={() => proceedWithRole('front')} disabled={frontCount === 0}
                                    className="flex flex-col items-start rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-600 dark:hover:bg-blue-950/30">
                                    <span className="text-xs font-semibold text-gray-800 dark:text-slate-100">Fronts</span>
                                    <span className="mt-0.5 text-[11px] text-gray-500 dark:text-slate-400">{frontCount} device{frontCount !== 1 ? 's' : ''}</span>
                                </button>
                                <button type="button" onClick={() => proceedWithRole('rear')} disabled={rearCount === 0}
                                    className="flex flex-col items-start rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-600 dark:hover:bg-blue-950/30">
                                    <span className="text-xs font-semibold text-gray-800 dark:text-slate-100">Rears</span>
                                    <span className="mt-0.5 text-[11px] text-gray-500 dark:text-slate-400">{rearCount} device{rearCount !== 1 ? 's' : ''}</span>
                                </button>
                            </div>
                            <div className="flex justify-end">
                                <button type="button" onClick={onClose} disabled={submitting} className={BTN_GHOST}>Dismiss</button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
                            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

                                {/* Operation */}
                                <div>
                                    <label className={LABEL_CLS}>Operation</label>
                                    <select className={SELECT_CLS} value={operation}
                                        onChange={(e) => {
                                            const next = e.target.value as CommandOperation;
                                            setOperation(next);
                                            if (next !== CMD_FW_DWNLD) setTargetVersion('');
                                            if (next !== CMD_LOG_UPLD) { setLogUploadIndexMode('neither'); setLogUploadRaceId(''); }
                                        }}>
                                        {opGroups.map((g) => (
                                            <optgroup key={g.label} label={g.label}>
                                                {g.options.map((op) => (
                                                    <option key={op.value} value={op.value}>{op.label}</option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </div>

                                {/* Firmware version */}
                                {isFirmwareOp && (
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className={LABEL_CLS} style={{ marginBottom: 0 }}>Firmware version</label>
                                            <RefreshButton busy={fwLoading}
                                                onClick={() => void refreshFwVersions(commandRole === 'front' ? 'front' : 'rear')}
                                                className="text-[11px] text-blue-500 hover:underline dark:text-blue-400"
                                                busyText="Loading…"
                                                busyClassName="text-[11px] text-gray-400 dark:text-slate-500" />
                                        </div>
                                        <select className={SELECT_CLS} value={targetVersion}
                                            onChange={(e) => setTargetVersion(e.target.value)}
                                            disabled={fwLoading || !fwTargets.length}>
                                            <option value="">{fwLoading ? 'Loading…' : 'Select version…'}</option>
                                            {fwTargets.map((row) => (
                                                <option key={`${row.role}:${row.targetVersion}`} value={row.targetVersion}>
                                                    {row.version} — {row.fileName}
                                                </option>
                                            ))}
                                        </select>
                                        {fwError && <p className="mt-1 text-[11px] text-red-500">{fwError}</p>}
                                    </div>
                                )}

                                {/* Modem DFOTA package */}
                                {isModemDfotaOp && (
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className={LABEL_CLS} style={{ marginBottom: 0 }}>Modem firmware package</label>
                                            <RefreshButton busy={modemFwLoading}
                                                onClick={() => void refreshModemFwPackages()}
                                                className="text-[11px] text-blue-500 hover:underline dark:text-blue-400"
                                                busyText="Loading…"
                                                busyClassName="text-[11px] text-gray-400 dark:text-slate-500" />
                                        </div>
                                        <select className={SELECT_CLS} value={modemFwUrl}
                                            onChange={(e) => setModemFwUrl(e.target.value)}
                                            disabled={modemFwLoading || !modemFwTargets.length}>
                                            <option value="">{modemFwLoading ? 'Loading…' : 'Select package…'}</option>
                                            {modemFwTargets.map((row) => (
                                                <option key={row.key || row.url} value={row.url}>
                                                    {row.fileName}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="mt-1 text-[11px] text-gray-400 dark:text-slate-500">
                                            Quectel .par delta packages hosted on CloudFront.
                                        </p>
                                        {modemFwError && <p className="mt-1 text-[11px] text-red-500">{modemFwError}</p>}
                                    </div>
                                )}

                                {/* Log type */}
                                {isLogKindOp && !(operation === CMD_DELETE_LOGS && commandRole === 'front') && (
                                    <div>
                                        <label className={LABEL_CLS}>Log type</label>
                                        <div className="flex gap-2">
                                            {([LOG_RACE, LOG_DEBUG] as logKind[]).map((k) => (
                                                <button key={k} type="button"
                                                    onClick={() => setLogKind(k)}
                                                    className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                                                        logKind === k
                                                            ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-300'
                                                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                                                    }`}>
                                                    {k === LOG_RACE ? 'Race' : 'Debug'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Admin: index uploads under */}
                                {isAdmin && isLogUploadOp && (
                                    <div>
                                        <label className={LABEL_CLS}>Index uploads under</label>
                                        <div className="space-y-1">
                                            {(['event', 'folder', 'neither'] as AdminLogUploadIndexMode[]).map((mode) => {
                                                const labels: Record<AdminLogUploadIndexMode, string> = { event: 'Event', folder: 'Folder name', neither: 'Neither (fleet-wide)' };
                                                return (
                                                    <label key={mode} className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-800">
                                                        <input type="radio" name="admin-log-upload-index"
                                                            checked={logUploadIndexMode === mode}
                                                            onChange={() => applyAdminLogUploadIndexMode(mode)}
                                                            className="accent-blue-600" />
                                                        <span className="text-xs text-gray-700 dark:text-slate-300">{labels[mode]}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>

                                        {logUploadIndexMode === 'event' && (
                                            <div className="mt-2 space-y-2 pl-2">
                                                <select className={SELECT_CLS} value={adminOptionalEventId}
                                                    onChange={(e) => setAdminOptionalEventId(e.target.value)}
                                                    disabled={adminEventsLoading}>
                                                    <option value="">Select event…</option>
                                                    {adminEvents.map((ev) => (
                                                        <option key={ev.eventId} value={ev.eventId}>
                                                            {ev.name ? `${ev.name} (${ev.eventId})` : ev.eventId}
                                                        </option>
                                                    ))}
                                                </select>
                                                {adminEventsError && <p className="text-[11px] text-red-500">{adminEventsError}</p>}
                                                <select className={SELECT_CLS} value={logUploadRaceId}
                                                    onChange={(e) => setLogUploadRaceId(e.target.value)}
                                                    disabled={!adminOptionalEventId || logUploadRacesLoading}>
                                                    <option value="">All races (event-level)</option>
                                                    {logUploadRaces.map((race) => (
                                                        <option key={race.raceId} value={race.raceId}>
                                                            {race.name ? `${race.name} (${race.raceId})` : race.raceId}
                                                        </option>
                                                    ))}
                                                </select>
                                                {logUploadRacesError && <p className="text-[11px] text-red-500">{logUploadRacesError}</p>}
                                            </div>
                                        )}

                                        {logUploadIndexMode === 'folder' && (
                                            <div className="mt-2 space-y-2 pl-2">
                                                <div className="flex gap-2">
                                                    {(['existing', 'new'] as const).map((m) => (
                                                        <button key={m} type="button"
                                                            onClick={() => setLogFolderMode(m)}
                                                            className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                                                                logFolderMode === m
                                                                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-300'
                                                                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'
                                                            }`}>
                                                            {m === 'existing' ? 'Existing' : 'New'}
                                                        </button>
                                                    ))}
                                                </div>
                                                {logFolderMode === 'existing' && (
                                                    <>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-[11px] text-gray-500">{existingLogFolders.length} folder{existingLogFolders.length !== 1 ? 's' : ''}</span>
                                                            <RefreshButton busy={logFoldersLoading} onClick={() => void refreshExistingLogFolders()}
                                                                className="text-[11px] text-blue-500 hover:underline" busyText="Loading…" busyClassName="text-[11px] text-gray-400" />
                                                        </div>
                                                        <select className={SELECT_CLS} value={existingLogFolder}
                                                            onChange={(e) => setExistingLogFolder(e.target.value)}
                                                            disabled={logFoldersLoading || existingLogFolders.length === 0}>
                                                            <option value="">{logFoldersLoading ? 'Loading…' : 'Select folder…'}</option>
                                                            {existingLogFolders.map((f) => <option key={f} value={f}>{f}</option>)}
                                                        </select>
                                                        {logFoldersError && <p className="text-[11px] text-red-500">{logFoldersError}</p>}
                                                    </>
                                                )}
                                                {logFolderMode === 'new' && (
                                                    <>
                                                        <input className={INPUT_CLS} placeholder="e.g. race-day-1" value={newLogFolder} onChange={(e) => setNewLogFolder(e.target.value)} />
                                                        <p className="text-[11px] text-gray-400 dark:text-slate-500">Letters, numbers, dot, underscore, hyphen · max 64 chars</p>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Operator: race + folder */}
                                {isLogUploadOp && !isAdmin && (
                                    <div className="space-y-3">
                                        {String(operatorScopedEventId ?? '').trim() && (
                                            <div>
                                                <label className={LABEL_CLS}>Race (optional)</label>
                                                <select className={SELECT_CLS} value={logUploadRaceId}
                                                    onChange={(e) => setLogUploadRaceId(e.target.value)}
                                                    disabled={logUploadRacesLoading}>
                                                    <option value="">All races</option>
                                                    {logUploadRaces.map((race) => (
                                                        <option key={race.raceId} value={race.raceId}>
                                                            {race.name ? `${race.name} (${race.raceId})` : race.raceId}
                                                        </option>
                                                    ))}
                                                </select>
                                                {logUploadRacesError && <p className="mt-1 text-[11px] text-red-500">{logUploadRacesError}</p>}
                                            </div>
                                        )}
                                        <div>
                                            <label className={LABEL_CLS}>Log folder (optional)</label>
                                            <div className="flex gap-2">
                                                {(['none', 'existing', 'new'] as const).map((m) => (
                                                    <button key={m} type="button"
                                                        onClick={() => setLogFolderMode(m)}
                                                        className={`flex-1 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors ${
                                                            logFolderMode === m
                                                                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-300'
                                                                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'
                                                        }`}>
                                                        {m === 'none' ? 'None' : m === 'existing' ? 'Existing' : 'New'}
                                                    </button>
                                                ))}
                                            </div>
                                            {logFolderMode === 'existing' && (
                                                <div className="mt-2 space-y-1">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-[11px] text-gray-500">{existingLogFolders.length} folder{existingLogFolders.length !== 1 ? 's' : ''}</span>
                                                        <RefreshButton busy={logFoldersLoading} onClick={() => void refreshExistingLogFolders()}
                                                            className="text-[11px] text-blue-500 hover:underline" busyText="Loading…" busyClassName="text-[11px] text-gray-400" />
                                                    </div>
                                                    <select className={SELECT_CLS} value={existingLogFolder}
                                                        onChange={(e) => setExistingLogFolder(e.target.value)}
                                                        disabled={logFoldersLoading || existingLogFolders.length === 0}>
                                                        <option value="">{logFoldersLoading ? 'Loading…' : 'Select folder…'}</option>
                                                        {existingLogFolders.map((f) => <option key={f} value={f}>{f}</option>)}
                                                    </select>
                                                    {logFoldersError && <p className="text-[11px] text-red-500">{logFoldersError}</p>}
                                                </div>
                                            )}
                                            {logFolderMode === 'new' && (
                                                <div className="mt-2 space-y-1">
                                                    <input className={INPUT_CLS} placeholder="e.g. race-day-1" value={newLogFolder} onChange={(e) => setNewLogFolder(e.target.value)} />
                                                    <p className="text-[11px] text-gray-400 dark:text-slate-500">Letters, numbers, dot, underscore, hyphen · max 64 chars</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Target devices */}
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className={LABEL_CLS} style={{ marginBottom: 0 }}>
                                            Devices <span className="normal-case font-normal text-gray-400">({targetDeviceIds.length})</span>
                                        </label>
                                        <input className="w-40 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                            placeholder="Filter…"
                                            value={targetSearch}
                                            onChange={(e) => setTargetSearch(e.target.value)} />
                                    </div>

                                    {membershipCheckActive && (
                                        <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px]">
                                            {deviceMembershipLoading ? (
                                                <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-slate-400">
                                                    <InlineSpinner size="xs" />
                                                    Checking device registration…
                                                </span>
                                            ) : deviceMembershipError ? (
                                                <span className="inline-flex items-center gap-1.5 text-red-500">
                                                    <AlertTriangle className="h-3 w-3 shrink-0" />
                                                    {deviceMembershipError}
                                                </span>
                                            ) : unregisteredTargetIds.length > 0 ? (
                                                <span className="inline-flex items-center gap-1.5 font-medium text-amber-600 dark:text-amber-400">
                                                    <AlertTriangle className="h-3 w-3 shrink-0" />
                                                    {targetDeviceIds.length - unregisteredTargetIds.length}/{targetDeviceIds.length} registered to {membershipDestinationLabel}
                                                </span>
                                            ) : (
                                                <span className="text-emerald-600 dark:text-emerald-400">
                                                    All devices registered to {membershipDestinationLabel}
                                                </span>
                                            )}
                                            {!deviceMembershipLoading && deviceMembershipError && (
                                                <RefreshButton busy={deviceMembershipLoading}
                                                    onClick={() => void refreshDeviceMembership()}
                                                    className="text-[11px] text-blue-500 hover:underline dark:text-blue-400"
                                                    busyText="Loading…" busyClassName="text-[11px] text-gray-400" />
                                            )}
                                            {!deviceMembershipLoading && unregisteredTargetIds.length > 0 && (
                                                <button type="button" onClick={removeUnregisteredDevices}
                                                    className="shrink-0 text-[11px] font-medium text-red-500 hover:underline dark:text-red-400">
                                                    Remove {unregisteredTargetIds.length} unregistered
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <div className="max-h-36 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-800/60">
                                        {filteredTargetDeviceIds.length === 0 ? (
                                            <p className="px-3 py-2 text-[11px] text-gray-400 dark:text-slate-500">No devices.</p>
                                        ) : (
                                            <ul>
                                                {filteredTargetDeviceIds.map((id) => {
                                                    const isUnregistered = unregisteredTargetIdSet.has(id);
                                                    return (
                                                        <li key={id}
                                                            title={isUnregistered ? `Not registered to ${membershipDestinationLabel}` : undefined}
                                                            className={`flex items-center justify-between px-3 py-1 text-xs ${
                                                                isUnregistered
                                                                    ? 'bg-red-50 dark:bg-red-950/30'
                                                                    : 'odd:bg-white even:bg-gray-50 dark:odd:bg-slate-800/40 dark:even:bg-slate-800/60'
                                                            }`}>
                                                            <span className="inline-flex min-w-0 items-center gap-1.5">
                                                                {isUnregistered && <AlertTriangle className="h-3 w-3 shrink-0 text-red-500" />}
                                                                <span className={`truncate font-mono ${isUnregistered ? 'text-red-700 dark:text-red-300' : 'text-gray-700 dark:text-slate-300'}`}>{id}</span>
                                                            </span>
                                                            <button type="button"
                                                                onClick={() => removeTargetDevice(id)}
                                                                className="ml-2 shrink-0 text-[10px] text-gray-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400">
                                                                ✕
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                </div>

                            </div>

                            {/* Footer */}
                            <div className="h-px bg-gray-100 dark:bg-slate-800" />
                            <div className="flex items-center gap-2 px-5 py-3">
                                {showRoleChoice && (
                                    <button type="button" onClick={() => setRoleStep('choose')} disabled={submitting}
                                        className={BTN_GHOST + ' mr-auto'}>
                                        ← Back
                                    </button>
                                )}
                                <div className="ml-auto flex gap-2">
                                    <button type="button" onClick={onClose} disabled={submitting} className={BTN_GHOST}>Dismiss</button>
                                    <button type="submit" disabled={submitDisabled}
                                        aria-busy={submitting}
                                        aria-label={submitting ? 'Submit in progress' : undefined}
                                        title={
                                            membershipBlocked
                                                ? deviceMembershipError
                                                    ? 'Could not verify device registration — retry before submitting.'
                                                    : deviceMembershipLoading
                                                        ? 'Checking device registration…'
                                                        : `Remove the ${unregisteredTargetIds.length} device${unregisteredTargetIds.length === 1 ? '' : 's'} not registered to ${membershipDestinationLabel} before submitting.`
                                                : undefined
                                        }
                                        className="inline-flex min-w-[5.5rem] items-center justify-center gap-1.5 rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500">
                                        {submitting ? (
                                            <>
                                                <InlineSpinner size="xs" className="border-white border-t-transparent" />
                                                Submitting…
                                            </>
                                        ) : (
                                            'Submit'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </PortaledLayer>
    );
}
